import { useEffect, useMemo, useState } from 'react';
import { InnerButton } from '@/modules/InnerButton';
import { PaginationProps } from '@/shared/types/pagination';
import { OPTIONS } from '../../constants/options';
import { Dropdown } from '@/modules/Dropdown';
import {
  faAngleDoubleLeft,
  faAngleDoubleRight,
  faAngleLeft,
  faAngleRight,
} from '@fortawesome/pro-regular-svg-icons';

/**
 * Pagination - Componente de paginación
 *
 * Componente de paginación que permite navegar entre páginas de datos.
 * Soporta selector de tamaño de página, contador de páginas visibles y navegación con botones.
 *
 * @example
 * ```tsx
 * // Paginación básica
 * <Pagination
 *   currentPage={currentPage}
 *   pageCount={totalPages}
 *   total={totalItems}
 *   onPageChange={(page) => setCurrentPage(page)}
 * />
 *
 * // Paginación con selector de tamaño
 * <Pagination
 *   currentPage={currentPage}
 *   pageCount={totalPages}
 *   total={totalItems}
 *   pageSize={pageSize}
 *   showPagesSelector
 *   onPageChange={(page) => setCurrentPage(page)}
 *   handlePageSize={(size) => setPageSize(size)}
 * />
 * ```
 *
 * @see {@link PaginationProps} para la lista completa de props
 */
export const Pagination = ({
  currentPage,
  pageCount,
  pageSize = 10,
  total,
  pageSizeText = '',
  showingPagesText = 'Mostrando página',
  optionsSize = OPTIONS,
  onPageChange,
  handlePageSize = () => {},
  className = '',
  maxVisiblePages = 5,
  showVisiblePages = false,
  showPagesSelector = false,
  showPagesCount = false,
}: PaginationProps) => {
  useEffect(() => {
    if (currentPage > pageCount && pageCount > 0) {
      onPageChange(pageCount);
    }
  }, [currentPage, pageCount, onPageChange]);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= pageCount) {
      onPageChange(page);
    }
  };

  const [currentPageSize, setCurrentPageSize] = useState(pageSize);

  useEffect(() => {
    setCurrentPageSize(pageSize);
  }, [pageSize]);

  const pagesCount = Math.min(currentPage * (pageSize || 0), total || 0) || 0;

  // Calcular el mínimo tamaño de página de las opciones disponibles
  const minPageSize = useMemo(() => {
    if (!optionsSize || optionsSize.length === 0) return 5;
    return Math.min(...optionsSize.map((opt) => Number(opt.id) || 5));
  }, [optionsSize]);

  const getVisiblePages = () => {
    const start = Math.min(Math.max(currentPage, 1), Math.max(pageCount - maxVisiblePages + 1, 1));
    const end = Math.min(start + maxVisiblePages - 1, pageCount);

    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  const visiblePages = getVisiblePages();

  const handlerPageSize = (pageSize: number) => {
    setCurrentPageSize(pageSize);
    handlePageSize(pageSize);
    handlePageChange(1);
  };

  return (
    <div
      className={`ui-imj ui-bottom-0 ui-left-0 ui-right-0 ui-w-full ui-px-8 ui-pt-4 ${className}`}
      role="navigation"
      aria-label="Paginación"
    >
      <div className="ui-grid ui-w-full ui-grid-cols-[1fr] ui-grid-rows-3 ui-items-center ui-justify-center ui-gap-x-32 ui-gap-y-2 md:ui-grid-cols-[1fr_auto_1fr] md:ui-grid-rows-1 md:ui-justify-between">
        {showVisiblePages && pageCount > 1 ? (
          <p className="ui-text-center ui-text-body-xs-bold ui-font-body-xs-bold ui-tracking-body-lg ui-text-tertiary md:ui-text-right">
            {showingPagesText} {currentPage} de {pageCount}
            {total && showPagesCount && (
              <span className="ui-ml-2">
                ({pagesCount}-{pagesCount} de {total})
              </span>
            )}
          </p>
        ) : (
          <div></div>
        )}

        {pageCount > 0 ? (
          <nav
            className="ui-flex ui-justify-center ui-gap-1 md:ui-justify-start"
            aria-label="Navegación de páginas"
          >
            <InnerButton
              theme="solid"
              size="xs"
              pill="pill"
              color="secondary"
              iconProps={{ icon: faAngleLeft }}
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              aria-label="Página anterior"
              tooltipProps={{ label: 'Página anterior', position: 'top-right' }}
            />
            <InnerButton
              theme="solid"
              size="xs"
              pill="pill"
              color="secondary"
              iconProps={{ icon: faAngleDoubleLeft }}
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
              aria-label="Primera página"
              tooltipProps={{ label: 'Primera página', position: 'top-left' }}
            />

            {visiblePages.map((page) => (
              <InnerButton
                key={page}
                square
                onClick={() => handlePageChange(page)}
                whiteBackground={currentPage === page}
                size="xs"
                theme="solid"
                pill="pill"
                color="secondary"
                disabled={pageCount === 1}
                active={currentPage === page}
                aria-label={`Ir a la página ${page}`}
                aria-current={currentPage === page ? 'page' : undefined}
              >
                {page}
              </InnerButton>
            ))}

            <InnerButton
              theme="solid"
              size="xs"
              pill="pill"
              color="secondary"
              iconProps={{ icon: faAngleRight }}
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === pageCount}
              aria-label="Página siguiente"
              tooltipProps={{ label: 'Página siguiente', position: 'top-left' }}
            />
            <InnerButton
              theme="solid"
              size="xs"
              pill="pill"
              color="secondary"
              iconProps={{ icon: faAngleDoubleRight }}
              onClick={() => handlePageChange(pageCount)}
              disabled={currentPage === pageCount}
              aria-label="Última página"
              tooltipProps={{ label: 'Última página', position: 'top-right' }}
            />
          </nav>
        ) : (
          <div></div>
        )}

        <div className="ui-flex ui-w-fit ui-items-center ui-justify-center md:ui-justify-end">
          {showPagesSelector && total && total > minPageSize && (
            <>
              <label
                htmlFor="page-size"
                className="ui-text-xxs ui-tracking-wider ui-text-neutral-700"
              >
                {pageSizeText}
              </label>
              <Dropdown
                field={{ size: 'xs' }}
                tooltip={{ tooltipProps: { label: 'Tamaño de página' } }}
                items={{ options: optionsSize, idName: 'id' }}
                values={{ value: currentPageSize }}
                events={{ onValueChange: (value) => handlerPageSize(Number(value)) }}
                multi={{ showTriggerClearButton: false }}
                className="ui-min-w-[68px]"
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};
